import React, {Component} from 'react';
import {
  syncStyleSheet,
  syncSwatchesToCssVariables,
  syncVariablesToCssVariables,
} from '../utils/StyleSheetUtils';
import type {OpenCanvasStyleSheetOptions} from '@packages/systems/open-canvas/core';
import {webflowizeStyleSheet} from '@packages/systems/designer/styles';
import {isEqual} from 'lodash';
import {is} from 'immutable';

import {getComputedStyles} from '@packages/systems/style-legacy/utils';
import {getNativeNodeByNativeId} from '@packages/systems/core/utils/NativeIdUtils';

import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import type {StyleSheetImageResolverInterface} from '@packages/systems/designer/assets';
import {
  type CssVariablesStateType,
  getLiveVariables,
} from '@packages/systems/core/utils/StyleTypes';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import type {Font} from '@packages/systems/core/constants/Fonts';
import {Plugins} from '@packages/systems/wfdl';
import {WebflowCssProperty} from '@packages/systems/core/utils/WebflowCssSubset';
import {VariantDataMap} from '@packages/systems/style-legacy/selectors';

type Props = {
  styleBlockState: StyleBlockStateType;
  cssVariablesState: CssVariablesStateType;
  variantDataMap: VariantDataMap;
  styleSheetImageResolver: StyleSheetImageResolverInterface;
  isEphemeralChangeInProgress: boolean;
  selectedNodeNativeId: string | null;
  styleSheetRendered: (arg0: {
    computedStyle: Partial<Record<WebflowCssProperty, string>>;
  }) => void;
  fonts: Font[];
  slugMap: Plugins.PluginsType['slugMap'];
  /**
   * Controls whether styles are rendered inline within <style> tags or injected into CSSOM.
   *
   * When `true`: Styles are rendered as text content within the <style> tags
   * When `false`: Styles are only injected into CSSOM (CSSStyleSheet)
   *
   * This is needed when custom code modifies the document head outside of React's
   * control, which can cause styles injected via CSSOM to be lost. In such cases,
   * falling back to inline styles ensures style persistence.
   */
  renderStyles?: boolean;
  isOpenCanvasActive?: boolean;
  openCanvasStyleSheetOptions?: OpenCanvasStyleSheetOptions | null;
  isElementStylesEnabled: boolean;
};

type State = {
  styles: string;
};

// The id is need to get the new style tag when the head is modified
// outside of react, usually by executing custom code in preview mode
const STYLE_ID = 'main-styles';

export default class CanvasStyleSheet extends Component<Props, State> {
  declare styleSheet: CSSStyleSheet;

  constructor(props: Props) {
    super(props);
    this.state = {
      styles: '',
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      !is(this.props.styleBlockState, nextProps.styleBlockState) ||
      !is(
        this.props.styleSheetImageResolver,
        nextProps.styleSheetImageResolver
      ) ||
      !is(this.props.cssVariablesState, nextProps.cssVariablesState) ||
      /**
       * NOTE: We need to use isEqual here because the variantDataMap is _built in_ JS Map
       * and not an Immutable Map. `is` checks equality _only_ for _Immutable_ Maps.
       */
      !isEqual(this.props.variantDataMap, nextProps.variantDataMap) ||
      !is(this.props.slugMap, nextProps.slugMap) ||
      !is(this.props.isOpenCanvasActive, nextProps.isOpenCanvasActive)
    ) {
      this.updateStyles(nextProps);
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    /**
     * Only trigger a re-render if styles are being rendered within the style tags and the styles have changed
     */
    return (
      Boolean(nextProps.renderStyles) &&
      Boolean(nextState.styles) &&
      this.state.styles !== nextState.styles
    );
  }

  updateStyles(props: Props) {
    const {
      styleBlockState,
      styleSheetImageResolver,
      cssVariablesState,
      isEphemeralChangeInProgress,
      slugMap,
      variantDataMap,
      isOpenCanvasActive,
      isElementStylesEnabled,
      openCanvasStyleSheetOptions,
    } = props;

    const firstSync = this.props === props;
    const didOpenCanvasModeChange =
      !firstSync && this.props.isOpenCanvasActive !== isOpenCanvasActive;

    if (this.styleSheet) {
      // IMPORTANT: We actually need `syncStyleSheet` to run before we can
      // safely run `syncVariablesToCssVariables`. We _probably_ should combine
      // these functions into a single one to ensure this ordering.
      syncStyleSheet(this.styleSheet, {
        styleBlockState,
        styleSheetImageResolver,
        cssVariablesState,
        slugMap,
        variantDataMap,
        isOpenCanvasActive,
        openCanvasStyleSheetOptions,
        wasOpenCanvasActive: this.props.isOpenCanvasActive,
        isElementStylesEnabled,
      });
      syncSwatchesToCssVariables(this.styleSheet, {
        oldSwatches: firstSync ? [] : this.props.cssVariablesState.swatches,
        newSwatches: cssVariablesState.swatches,
      });
      syncVariablesToCssVariables(this.styleSheet, {
        oldVariables:
          firstSync || didOpenCanvasModeChange
            ? StyleVariables.emptyVariableMap
            : getLiveVariables(this.props.cssVariablesState),
        newVariables: getLiveVariables(cssVariablesState),
        oldVariableCollections:
          firstSync || didOpenCanvasModeChange
            ? StyleVariables.emptyVariableCollectionMap
            : this.props.cssVariablesState.variableCollections,
        newVariableCollections: cssVariablesState.variableCollections,
        fonts: this.props.fonts,
        oldSlugMap: this.props.slugMap,
        newSlugMap: slugMap,
        isOpenCanvasActive,
      });

      const styles = [...this.styleSheet.cssRules]
        .map((rule) => rule.cssText)
        .join('\n');
      this.setState({styles});

      const nativeId = this.props.selectedNodeNativeId;
      if (nativeId && !isEphemeralChangeInProgress) {
        const nativeNode = getNativeNodeByNativeId(nativeId);
        if (nativeNode == null) {
          // The selected element has no live DOM node in the canvas iframe
          // (e.g. Commerce dynamic-template elements like
          // CommerceAddToCartOptionPill when no product variants are
          // materialized, or curated empty CMS Collection Item children).
          // Calling getComputedStyles(null) would return an emptyObject,
          // which the StyleStore would then feed to buildStyleMap and emit
          // a flood of "Could not find style object for property" errors
          // while clobbering the panel state. CanvasBody is the authoritative
          // signal for the no-DOM gate (it dispatches CANVAS_BODY_RENDERED
          // with no computedStyle, which the StyleStore reducer interprets
          // as no-DOM via the lastSyncedNodeNativeId check). Skipping this
          // dispatch is correct: there is nothing to sync. @see STYL-2308,
          // CMSAUTH-5280.
          return;
        }
        this.props.styleSheetRendered({
          computedStyle: getComputedStyles(
            nativeNode,
            cssVariablesState.variableCollections,
            [],
            cssVariablesState.variables,
            slugMap
          ),
        });
      }
    }
  }

  setStyleSheet = (element: HTMLStyleElement | null) => {
    if (element) {
      const document = element.ownerDocument;
      this.styleSheet = webflowizeStyleSheet(document, element);
      this.updateStyles(this.props);
    }
  };

  render() {
    return (
      <style
        type="text/css"
        rel="stylesheet"
        id={STYLE_ID}
        ref={this.setStyleSheet}
      >
        {this.props.renderStyles ? this.state.styles : ''}
      </style>
    );
  }
}
